import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { Loader2, MapPin } from "lucide-react";
import api from "../lib/api";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

export default function Dashboard() {
  const navigate = useNavigate();
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const navRef = useRef(navigate);
  navRef.current = navigate;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // null = all stages shown

  useEffect(() => {
    api.get("/crm/map").then((r) => r.data).catch(() => ({ points: [], stages: [] }))
      .then((d) => { setData(d); setLoading(false); });
  }, []);

  // Initialize the filter to "all stages" once data arrives.
  useEffect(() => {
    if (data && active === null) setActive((data.stages || []).map((s) => s.label));
  }, [data, active]);

  // Apply the stage filter to both the points and heatmap layers.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || active === null) return;
    const filt = active.length
      ? ["in", ["get", "stage"], ["literal", active]]
      : ["==", ["get", "stage"], "__none__"];
    const apply = () => {
      if (map.getLayer("pts")) map.setFilter("pts", filt);
      if (map.getLayer("heat")) map.setFilter("heat", filt);
    };
    if (map.getLayer("pts")) apply();
    else map.once("load", apply);
  }, [active, data]);

  const toggleStage = (label) =>
    setActive((prev) => {
      const cur = prev || [];
      return cur.includes(label) ? cur.filter((l) => l !== label) : [...cur, label];
    });
  const stageOn = (label) => active === null || active.includes(label);

  useEffect(() => {
    if (!data || mapRef.current || !mapEl.current) return;
    const fc = {
      type: "FeatureCollection",
      features: (data.points || []).map((p) => ({
        type: "Feature",
        geometry: { type: "Point", coordinates: [p.lng, p.lat] },
        properties: { name: p.name, stage: p.stage, color: p.color, status: p.status, open_path: p.open_path },
      })),
    };
    const map = new mapboxgl.Map({
      container: mapEl.current,
      style: "mapbox://styles/mapbox/dark-v11",
      projection: "mercator",
      bounds: [[-125, 24], [-66.5, 49.5]],
      fitBoundsOptions: { padding: 20 },
      attributionControl: false,
    });
    mapRef.current = map;
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");

    map.on("load", () => {
      map.addSource("props", { type: "geojson", data: fc });
      map.addLayer({
        id: "heat", type: "heatmap", source: "props", maxzoom: 15,
        paint: {
          "heatmap-weight": 1,
          "heatmap-intensity": ["interpolate", ["linear"], ["zoom"], 0, 0.6, 12, 2],
          "heatmap-radius": ["interpolate", ["linear"], ["zoom"], 0, 18, 12, 45],
          "heatmap-opacity": ["interpolate", ["linear"], ["zoom"], 12, 0.55, 15, 0],
          "heatmap-color": ["interpolate", ["linear"], ["heatmap-density"],
            0, "rgba(0,0,0,0)", 0.2, "rgba(56,189,248,0.35)", 0.4, "rgba(167,139,250,0.5)",
            0.6, "rgba(245,158,11,0.6)", 1, "rgba(52,211,153,0.85)"],
        },
      });
      map.addLayer({
        id: "pts", type: "circle", source: "props",
        paint: {
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 3, 5, 10, 9],
          "circle-color": ["get", "color"],
          "circle-stroke-width": 1.5,
          "circle-stroke-color": "#0b0f0e",
          "circle-opacity": 0.9,
        },
      });
      const popup = new mapboxgl.Popup({ closeButton: false, offset: 12 });
      map.on("mouseenter", "pts", (e) => {
        map.getCanvas().style.cursor = "pointer";
        const f = e.features[0];
        popup.setLngLat(f.geometry.coordinates)
          .setHTML(`<div style="font:600 12px system-ui;color:#111">${f.properties.name}</div><div style="font:11px system-ui;color:#555">${f.properties.stage}${f.properties.status ? " · " + f.properties.status : ""}</div>`)
          .addTo(map);
      });
      map.on("mouseleave", "pts", () => { map.getCanvas().style.cursor = ""; popup.remove(); });
      map.on("click", "pts", (e) => {
        const path = e.features[0].properties.open_path;
        if (path) navRef.current(path);
      });
    });
    return () => { map.remove(); mapRef.current = null; };
  }, [data]);

  const total = data?.points?.length || 0;

  return (
    <div className="relative h-[calc(100vh-64px)] w-full" data-testid="dashboard-map">
      <div ref={mapEl} className="absolute inset-0 h-full w-full" />
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/60 z-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}
      {/* Title + legend overlay */}
      <div className="absolute bottom-6 left-4 z-10 rounded-xl border border-border bg-card/90 backdrop-blur px-4 py-3 shadow-lg" data-testid="map-legend">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-primary" />
          <h1 className="text-lg font-bold tracking-tight">Acquisition Map</h1>
          <span className="text-xs text-muted-foreground">{total} located</span>
        </div>
        <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1">
          {(data?.stages || []).map((s) => (
            <button
              key={s.label}
              type="button"
              onClick={() => toggleStage(s.label)}
              title={stageOn(s.label) ? `Hide ${s.label}` : `Show ${s.label}`}
              className={`flex items-center gap-1.5 text-xs rounded px-1 -mx-1 py-0.5 transition-opacity hover:bg-white/5 ${stageOn(s.label) ? "" : "opacity-35"}`}
              data-testid={`legend-${s.label.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
              <span className="text-muted-foreground">{s.label}</span>
              <span className="font-semibold ml-auto">{s.count}</span>
            </button>
          ))}
        </div>
        <div className="mt-1.5 text-[10px] text-muted-foreground/60">Click a stage to show/hide it on the map</div>
      </div>
      {!loading && total === 0 && (
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <p className="text-sm text-muted-foreground bg-card/90 px-4 py-2 rounded-lg border border-border">No located properties yet — add addresses to your records to see them here.</p>
        </div>
      )}
    </div>
  );
}
